import { hasCandidates } from './deriveStoreNudgeCandidates'
import {
  STORE_NUDGE_CONSOLIDATION_MS,
  STORE_NUDGE_COOLDOWN_MS
} from '../model/constants'


import type {
  StoreNudgeBatch,
  StoreNudgeDerivation,
  StoreNudgeEvent,
  StoreNudgeMachineState,
  StoreNudgeTimer
} from '../model/types'

type InitialStateInput = {
  /** ISO do último fechamento persistido; restaura o cooldown após recarga. */
  lastShownAt: string | null
  now: number
  gateOpen?: boolean
}

const idle = (gateOpen: boolean): StoreNudgeMachineState => ({
  phase: 'idle',
  since: null,
  until: null,
  pending: null,
  batch: null,
  gateOpen
})

export const initialState = ({
  lastShownAt,
  now,
  gateOpen = true
}: InitialStateInput): StoreNudgeMachineState => {
  const shownAt = lastShownAt ? Date.parse(lastShownAt) : NaN
  if (!Number.isNaN(shownAt) && now - shownAt < STORE_NUDGE_COOLDOWN_MS) {
    return { ...idle(gateOpen), phase: 'cooldown', until: shownAt + STORE_NUDGE_COOLDOWN_MS }
  }
  return idle(gateOpen)
}

/** Manchete: desbloqueio vence moedas por ser mais específico. */
export const composeBatch = (derivation: StoreNudgeDerivation): StoreNudgeBatch => ({
  kind: derivation.unlocked.length > 0 ? 'unlocked' : 'afford',
  ...derivation
})

const windowElapsed = (state: StoreNudgeMachineState, now: number) =>
  state.since !== null && now - state.since >= STORE_NUDGE_CONSOLIDATION_MS

const tryShow = (state: StoreNudgeMachineState, now: number): StoreNudgeMachineState => {
  if (state.phase !== 'collecting') return state
  if (!state.pending || !state.gateOpen || !windowElapsed(state, now)) return state
  return {
    ...state,
    phase: 'showing',
    since: null,
    batch: composeBatch(state.pending),
    pending: null
  }
}

/**
 * Máquina de tempo do nudge. Garante: um popup por vez, lote congelado em
 * `showing`, gate fechado nunca descarta (só espera) e cooldown persistido.
 *
 * `pending` é sempre a derivação mais recente com candidatos — ela é um
 * snapshot do estado, não um evento, então a última substitui as anteriores.
 */
export const reduce = (
  state: StoreNudgeMachineState,
  event: StoreNudgeEvent
): StoreNudgeMachineState => {
  switch (event.type) {
    case 'CANDIDATES': {
      const pending = hasCandidates(event.derivation) ? event.derivation : null
      if (pending === state.pending) return state
      if (state.phase === 'idle') {
        return pending
          ? { ...state, phase: 'collecting', since: event.now, pending }
          : state
      }
      if (state.phase === 'collecting') {
        if (!pending) return idle(state.gateOpen)
        return tryShow({ ...state, pending }, event.now)
      }
      return { ...state, pending }
    }

    case 'COLLECT_DONE':
      return tryShow(state, event.now)

    case 'GATE_CHANGED':
      return tryShow({ ...state, gateOpen: event.open }, event.now)

    case 'DISMISS':
      if (state.phase !== 'showing') return state
      return {
        ...state,
        phase: 'cooldown',
        batch: null,
        until: event.now + STORE_NUDGE_COOLDOWN_MS
      }

    case 'COOLDOWN_DONE': {
      if (state.phase !== 'cooldown') return state
      if (state.until !== null && event.now < state.until) return state
      const next = idle(state.gateOpen)
      return state.pending
        ? { ...next, phase: 'collecting', since: event.now, pending: state.pending }
        : next
    }

    default:
      return state
  }
}

/** Próximo timer que o hook deve instalar para este estado, ou nenhum. */
export const nextTimer = (
  state: StoreNudgeMachineState,
  now: number
): StoreNudgeTimer | null => {
  if (state.phase === 'collecting' && state.since !== null) {
    return {
      event: 'COLLECT_DONE',
      delayMs: Math.max(0, state.since + STORE_NUDGE_CONSOLIDATION_MS - now)
    }
  }
  if (state.phase === 'cooldown' && state.until !== null) {
    return { event: 'COOLDOWN_DONE', delayMs: Math.max(0, state.until - now) }
  }
  return null
}
